import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MailIcon from '@mui/icons-material/Mail';
import { Outlet } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Collapse, FormControlLabel, ListSubheader, Switch } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import WindowIcon from '@mui/icons-material/Window';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import GroupsIcon from '@mui/icons-material/Groups';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PlagiarismIcon from '@mui/icons-material/Plagiarism';
import InventoryIcon from '@mui/icons-material/Inventory';
import StarIcon from '@mui/icons-material/Star';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AndroidIcon from '@mui/icons-material/Android';
import CategoryIcon from '@mui/icons-material/Category';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import MessageIcon from '@mui/icons-material/Message';
import EmailIcon from '@mui/icons-material/Email';
import LeidenfrostLogo from 'components/common/LeidenfrostLogo';
import { useAuth } from 'providers/AuthContextProvider';
import TopBar from 'components/layout/dashboard/TopBar';

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    display: 'flex',            // Mantenemos flex para que el footer siga al fondo
    flexDirection: 'column',
    minHeight: '100vh',         // Importante para que el fondo cubra toda la pantalla

    padding: theme.spacing(4),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
     marginLeft: `-${300}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  ...theme.mixins.drawerHeader
}));

export default function DashboardLayout() {

  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const userRole = user?.acceso;
  const [unreadCount, setUnreadCount] = React.useState(0)
  const [logs, setLogs] = React.useState([])
  const [checked, setChecked] = React.useState(() => {
    if (userRole === 'viewer') return true; 
    const env = localStorage.getItem("api_env") || "production";
    return env === "production";
  });
  const [open, setOpen] = React.useState(true);
  const [routes, setRoutes] =  React.useState([
    {
      header:"HOME",
      rutas: [
        {
          path: "/dashboard/home",
          label: "Inicio",
          icon: <WindowIcon />,
          open: false,
          disabled: false
        },
      ]
    },
    {
      header:"OPERATIONAL",
      rutas: [       
        {
          path: "/dashboard/orders",
          label: "Orders",
          icon: <PersonIcon />,
          open: false,
          disabled: false
        },
        {
          path: "/dashboard/checkouts",
          label: "Checkouts",
          icon: <PersonIcon />,
          open: false,
          disabled: false
        },
        {
          path: "/dashboard/apps",
          label: "Apps",
          icon: <PersonIcon />,
          open: false,
          disabled: false
        },
      ]
    },
    {
      header:"CAMPAIGNS",
      rutas:[
      {
        path: "/dashboard/campaigns",
        label: "Campaigns",
        icon: <EventIcon />,
        open: false,
        disabled: false
      },
      {
        path: "/main/emails",
        label: "Emails",
        icon: <EmailIcon />,
        open: false,
        disabled: true
      },
      {
        path: "/main/push-notifications",
        label: "Notificaciones",
        icon: <NotificationsIcon />,
        open: false,
        disabled: true
      },
      {
        path: "/main/sms",
        label: "SMS",
        icon: <MessageIcon />,
        open: false,
        disabled: true
      },
    ]},
    {
      header:"USERS",
      rutas:[
        {
          path: "/dashboard/clients",
          label: "Clients",
          icon: <PersonOutlineIcon />,
          open: false,
          disabled: false
        },
        {
          path: "/dashboard/costumers",
          label: "Costumers",
          icon: <GroupsIcon />,
          open: false,
          disabled: false
        },
        {
          path: "/dashboard/sales-force",
          label: "SalesForce",
          icon: <GroupsIcon />,
          open: false,
          disabled: false
        },
        {
          path: "/dashboard/administrators",
          label: "Administrators",
          icon: <GroupsIcon />,
          open: false,
          disabled: false
        },
      ]
    },
    {
      header:"MARKETPLACE",
      rutas:[
        {
          label: "Banners",
          icon: <StarIcon />,
          open: false,
          disabled: false,
          children:[
            { 
              path: "/main/banners/main",
              label: "Principales"
            },
            { 
              path: "/main/banners/grid",
              label: "Grid"
            },
            { 
              path: "/main/banners/secondary",
              label: "Secundarios"
            },
  
          ]
        },
        {
          label: "Productos",
          icon: <InventoryIcon />,
          open: false,
          disabled: false,
          children:[
            { 
              path: "/main/products",
              label: "Base"
            },
            { 
              path: "/main/products/highlight",
              label: "Destacados"
            },
            { 
              path: "/main/products/recommended",
              label: "Recomendados"
            },
            { 
              path: "/main/products/exclusive-online",
              label: "Exclusivos en linea"
            },
            { 
              path: "/main/products/package",
              label: "Promociones"
            }
          ]
        },
        {
          path: "/main/categories",
          label: "Categorias",
          icon: <CategoryIcon />,
          open: false,
          disabled: false
        },
        {
          path: "/main/brands",
          label: "Marcas",
          icon: <BrandingWatermarkIcon />,
          open: false,
          disabled: false
        },
      ]
    },
    {
      header:"SYSTEM",
      rutas:[
        {
          path: "/main/app/information",
          label: "Informacion",
          icon: <PlagiarismIcon />,
          open: false,
          disabled: true
        }
      ]
    },
    {
      header:"EXPO",
      rutas:[
        {
          path: "/main/guests",
          label: "Pre-registro",
          icon: <AdminPanelSettingsIcon />,
          open: false,
          disabled: false
        },
      ]
    }
  ]);

  const getShiftStartDate = () => {
    const today = new Date();
    today.setDate(today.getDate() - 1); 
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0'); 
    return `${year}-${month}-${day}`; 
  };

  const [logStartDate, setLogStartDate] = React.useState(getShiftStartDate);


  const filteredRoutes = routes.map(section => {
    if (userRole !== 'viewer') return section;

    const allowedPaths = ['/dashboard/home'];
    const filteredSubRutas = section.rutas.filter(ruta => allowedPaths.includes(ruta.path));

    if (filteredSubRutas.length === 0) return null;

    return {
      ...section,
      rutas: filteredSubRutas
    };
  }).filter(Boolean);


  const handleChange = (event) => {
    const isDev = event.target.checked;
    setChecked(isDev);
    localStorage.setItem("api_env", isDev ? "production" : "development");
    window.location.reload(); 
  };
 
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleLink = (path, sectionHeader, routeLabel) => () => {
    setRoutes((prev) =>
      prev.map((s) => {
        if (s.header !== sectionHeader) return s;
        return {
          ...s,
          rutas: s.rutas.map((r) => 
            r.label === routeLabel ? { ...r, open: !r.open } : r
          ),
        };
      })
    );
    if (path) navigate(path);
  };

  const handleLink2 = (path, i, j)=>()=>{
    navigate(path);
  }

  React.useEffect(()=>{},[routes])

  React.useEffect(() => {
    if (userRole === 'viewer' && localStorage.getItem("api_env") !== "production") {
      localStorage.setItem("api_env", "production");
      window.location.reload();
    }
  }, [userRole]);

  React.useEffect(()=>{
    getLogs()
  },[logStartDate])

  const getLogs = async () => {
    let queryParams = null; // Asumimos default (null) desde el principio

    // Si el usuario sí seleccionó una fecha en el input
    if (logStartDate) {
      const queryStartTime = `${logStartDate}T20:30:00.000-06:00`;
      queryParams = { fromDate: queryStartTime };
    }

    //const response = await API.getLogs(queryParams);
    
    /*if (response.status === 200) {
      setLogs((prevLogs) => {
        if (prevLogs.length > 0 && response.data.length > prevLogs.length) {
          const nuevos = response.data.length - prevLogs.length;
          setUnreadCount((prevCount) => prevCount + nuevos);
        }
        return [...response.data];
      });
    }*/
  };

  const clearUnread = () => setUnreadCount(0);

  return (
    <Box sx={{ display: 'flex' }}>
      <TopBar open={open} handleDrawerOpen={handleDrawerOpen} unreadCount={unreadCount} clearUnread={clearUnread}/>
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader >
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
          <LeidenfrostLogo />  
        </DrawerHeader>
        <Divider />
          <List>
            {(userRole === 'admin' || userRole === 'messiah') && (
              <ListItem key={"li_sw_env"}>
                <FormControlLabel 
                  control={
                    <Switch 
                      checked={checked} 
                      onChange={handleChange} 
                    />
                  }
                  label={checked ? "Production" : "Development"}
                />
              </ListItem>
            )}
            {filteredRoutes.map((section, i) => (
              <React.Fragment key={`section-${i}`}>
                <ListSubheader >
                  {section.header}
                </ListSubheader>
                {section.rutas.map(({ icon, label, path, open, children, disabled }, j) => (
                  <React.Fragment key={path || j}>
                    <ListItemButton 
                      onClick={handleLink(path, section.header, label)}
                      disabled={disabled}
                      selected={window.location.pathname === path}
                    >
                      <ListItemIcon sx={{ color: 'inherit' }}> {icon} </ListItemIcon>
                      <ListItemText primary={label} />
                      {children && (open ? <ExpandLess /> : <ExpandMore />)}
                    </ListItemButton>

                    {children && (
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {children.map((item, k) => (
                            <ListItemButton 
                              key={item.path || k} 
                              onClick={handleLink2(item.path)} 
                              sx={{ pl: 4 }}
                              selected={window.location.pathname === item.path}
                            >
                              <ListItemIcon />
                              <ListItemText primary={item.label} />
                            </ListItemButton>
                          ))}
                        </List>
                      </Collapse>
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </List>
        <Divider />
          <List>
            {['About Us'].map((text, index) => (
              <ListItem key={text} disablePadding>
                <ListItemButton disabled={true}>
                  <ListItemIcon>
                    {index % 2 === 0 ? <InboxIcon /> : <MailIcon />}
                  </ListItemIcon>
                  <ListItemText primary={text} />
                </ListItemButton>
              </ListItem>
            ))}
            <ListItem key={"v-ey"} disablePadding>
              <ListItemButton>
                <ListItemText primary={"version 1.8"} />
              </ListItemButton>
            </ListItem>
          </List>
      </Drawer>
      <Main open={open} >
        <DrawerHeader />
        <Outlet/>
      </Main>

      {/*<ResizableBottomConsole logs={logs} logStartDate={logStartDate} setLogStartDate={setLogStartDate} getLogs={getLogs}/>*/}
    </Box>
  );
}
